// WOS-BUNDLE: the task drawer's Links section (Work OS task-detail.tsx LinksEditor). One
// `tasks.links` call lists the task's links read from this end, grouped: Relates to,
// Duplicates, Duplicated by, Blocks, Blocked by (the last two are hard dependencies). Each linked
// task is a row — number, title, status — that opens it, with a hover × that removes the link.
// A visible "Add link" opens a relation select and a task search (`tasks.search`). ⌘K's "Link to
// task…" opens the same picker (the `companyos:task-link` event), and ⌘K offers a typed task as a
// link target directly (shell/palette-work.tsx).
import {
  Button,
  DrawerSection,
  Input,
  NativeSelect,
  NativeSelectOption,
  Skeleton,
  toast,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LinkIcon, PlusIcon, XIcon } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import type { Board, TaskDetail } from "./view-engine";
import { boardsQuery, pendingAsNull, taskSearchQuery, workKeys } from "./work-queries";

export type TaskLink = NonNullable<
  Awaited<ReturnType<Api["client"]["tasks"]["links"]>>
>["data"][number];
export type LinkRelation = TaskLink["relation"];

/** The groups in the drawer's order, with how each reads. */
export const LINK_GROUPS: readonly { relation: LinkRelation; label: string; verb: string }[] = [
  { relation: "relates_to", label: "Relates to", verb: "Relates to" },
  { relation: "duplicates", label: "Duplicates", verb: "Duplicates" },
  { relation: "duplicated_by", label: "Duplicated by", verb: "Is duplicated by" },
  { relation: "blocks", label: "Blocks", verb: "Blocks" },
  { relation: "blocked_by", label: "Blocked by", verb: "Is blocked by" },
];

/** ⌘K's "Link to task…" asks the open drawer to show its link picker. */
export const LINK_EVENT = "companyos:task-link";

export const linksKey = (co: string, id: string) => [...workKeys.task(co, id), "links"] as const;

export const linksQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: linksKey(co, id),
    queryFn: async () =>
      (await pendingAsNull(() => api.client.tasks.links({ params: { co, id } })))?.data ?? null,
  });

/** The links grouped in drawer order (empty groups dropped). */
export function groupLinks(links: readonly TaskLink[]) {
  return LINK_GROUPS.map((g) => ({
    ...g,
    links: links
      .filter((l) => l.relation === g.relation)
      .sort((a, b) => a.task.number - b.task.number),
  })).filter((g) => g.links.length > 0);
}

/** Add and remove a link; both ends' drawers, the cards (blocked) and the task refetch. */
export function useLinkWrites(api: Api, co: string, taskId: string) {
  const qc = useQueryClient();
  const settle = (other?: string) => {
    void qc.invalidateQueries({ queryKey: linksKey(co, taskId) });
    void qc.invalidateQueries({ queryKey: workKeys.task(co, taskId) });
    if (other) void qc.invalidateQueries({ queryKey: workKeys.task(co, other) });
    void qc.invalidateQueries({ queryKey: [...workKeys.all(co), "board"] });
  };
  const add = useMutation({
    mutationFn: (v: { relation: LinkRelation; task_id: string }) =>
      api.client.tasks.addLink({ params: { co, id: taskId }, body: v }),
    onSuccess: (l) =>
      toast.add({
        title: `Linked: ${LINK_GROUPS.find((g) => g.relation === l.relation)?.verb ?? "Relates to"} #${l.task.number}`,
        type: "success",
      }),
    onSettled: (_d, _e, v) => settle(v.task_id),
  });
  const remove = useMutation({
    mutationFn: (v: { relation: LinkRelation; other: string }) =>
      api.client.tasks.removeLink({
        params: { co, id: taskId, item_id: v.other },
        query: { relation: v.relation },
      }),
    // Gone from the list at once; a refusal brings it back with the refetch.
    onMutate: (v) => {
      qc.setQueryData<TaskLink[] | null>(linksKey(co, taskId), (d) =>
        d ? d.filter((l) => !(l.relation === v.relation && l.task.id === v.other)) : d,
      );
    },
    onSettled: (_d, _e, v) => settle(v.other),
  });
  return { add, remove };
}

export function TaskLinksSection({
  api,
  company,
  task,
  onOpenTask,
}: {
  api: Api;
  company: CompanyAccess;
  task: TaskDetail;
  onOpenTask: (id: string) => void;
}) {
  const co = company.id;
  const links = useQuery(linksQuery(api, co, task.id));
  const boards = useQuery(boardsQuery(api, co));
  const writes = useLinkWrites(api, co, task.id);
  const [adding, setAdding] = useState(false);
  const section = useRef<HTMLElement>(null);
  useEffect(() => {
    const open = (e: Event) => {
      if ((e as CustomEvent<string>).detail !== task.id) return;
      setAdding(true);
      section.current?.scrollIntoView({ block: "nearest" });
    };
    window.addEventListener(LINK_EVENT, open);
    return () => window.removeEventListener(LINK_EVENT, open);
  }, [task.id]);
  // An API without the route yet: no section.
  if (links.data === null) return null;
  const rows = links.data ?? [];
  const statusName = (t: TaskLink["task"]) =>
    boards.data?.data
      .find((b: Board) => b.id === t.board_id)
      ?.status_set.find((s) => s.key === t.status)?.name ?? t.status;
  return (
    <section ref={section} aria-label="Links" data-slot="task-links" className="mt-3">
      <DrawerSection
        title={
          <span className="flex items-center gap-2">
            Links
            {rows.length ? (
              <span className="text-xs font-normal text-muted-foreground tabular-nums">
                {rows.length}
              </span>
            ) : null}
          </span>
        }
      >
        {links.isPending ? (
          <Skeleton className="h-9 w-full" />
        ) : (
          groupLinks(rows).map((g) => (
            <div key={g.relation} className="mb-2" data-link-group={g.relation}>
              <h4 className="m-0 mb-0.5 text-2xs font-medium tracking-wide text-muted-foreground uppercase">
                {g.label}
              </h4>
              <ul aria-label={g.label} className="m-0 flex list-none flex-col p-0">
                {g.links.map((l) => (
                  <li
                    key={l.task.id}
                    className="group/link flex min-h-9 items-center gap-2 border-b border-subtle"
                  >
                    <LinkIcon aria-hidden="true" className="size-3.5 shrink-0 text-faint" />
                    <button
                      type="button"
                      className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm"
                      onClick={() => onOpenTask(l.task.id)}
                    >
                      <span className="text-xs text-muted-foreground tabular-nums">
                        #{l.task.number}
                      </span>
                      <span
                        className={
                          l.task.completed_at
                            ? "min-w-0 truncate text-muted-foreground line-through hover:underline"
                            : "min-w-0 truncate hover:underline"
                        }
                      >
                        {l.task.title}
                      </span>
                    </button>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {statusName(l.task)}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label={`Remove the link to #${l.task.number} ${l.task.title}`}
                      className="opacity-0 group-hover/link:opacity-100 group-focus-within/link:opacity-100 pointer-coarse:opacity-100"
                      onClick={() =>
                        writes.remove.mutate({ relation: l.relation, other: l.task.id })
                      }
                    >
                      <XIcon aria-hidden="true" />
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
        {adding ? (
          <LinkPicker
            api={api}
            co={co}
            task={task}
            taken={rows}
            onAdd={(v) => writes.add.mutate(v, { onSuccess: () => setAdding(false) })}
            onCancel={() => setAdding(false)}
          />
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-8 gap-1.5 px-2 text-muted-foreground hover:text-foreground"
            onClick={() => setAdding(true)}
          >
            <PlusIcon aria-hidden="true" />
            Add link
          </Button>
        )}
      </DrawerSection>
    </section>
  );
}

function LinkPicker({
  api,
  co,
  task,
  taken,
  onAdd,
  onCancel,
}: {
  api: Api;
  co: string;
  task: TaskDetail;
  taken: readonly TaskLink[];
  onAdd: (v: { relation: LinkRelation; task_id: string }) => void;
  onCancel: () => void;
}) {
  const [q, setQ] = useState("");
  const [relation, setRelation] = useState<LinkRelation>("relates_to");
  const relationId = useId();
  const term = q.trim();
  const results = useQuery({ ...taskSearchQuery(api, co, term), enabled: term.length > 0 });
  const linked = new Set(taken.filter((l) => l.relation === relation).map((l) => l.task.id));
  const options = (results.data?.data ?? [])
    .filter((t) => t.id !== task.id && !linked.has(t.id))
    .slice(0, 6);
  return (
    <div data-slot="link-picker" className="mt-1 flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <label htmlFor={relationId} className="sr-only">
          Link relation
        </label>
        <NativeSelect
          id={relationId}
          className="w-40"
          value={relation}
          onChange={(e) => setRelation(e.target.value as LinkRelation)}
        >
          {LINK_GROUPS.map((g) => (
            <NativeSelectOption key={g.relation} value={g.relation}>
              {g.verb}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <Input
          aria-label="Search for a task to link"
          placeholder="Search tasks by title or #number…"
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") onCancel();
            if (e.key === "Enter" && options[0]) {
              e.preventDefault();
              onAdd({ relation, task_id: options[0].id });
            }
          }}
        />
        <Button variant="ghost" size="icon-sm" aria-label="Cancel adding a link" onClick={onCancel}>
          <XIcon aria-hidden="true" />
        </Button>
      </div>
      {term ? (
        <ul
          aria-label="Matching tasks"
          className="m-0 flex list-none flex-col rounded-md border border-border p-1"
        >
          {options.length ? (
            options.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted"
                  onClick={() => onAdd({ relation, task_id: t.id })}
                >
                  <LinkIcon aria-hidden="true" className="size-3.5 text-faint" />
                  <span className="text-xs text-muted-foreground tabular-nums">#{t.number}</span>
                  <span className="min-w-0 truncate">{t.title}</span>
                </button>
              </li>
            ))
          ) : (
            <li className="px-2 py-1.5 text-sm text-muted-foreground">
              {results.isPending ? "Searching…" : "No other task matches."}
            </li>
          )}
        </ul>
      ) : null}
    </div>
  );
}
